import axios, { AxiosInstance } from 'axios'

// Base configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/saas/microlearning`,
  withCredentials: true, // Include cookies for session authentication
  headers: {
    'Content-Type': 'application/json',
  },
})

// Separate API instance for measures endpoints
const measuresApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/saas/measures`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// API instance for organization measures endpoints
const orgMeasuresApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/saas/organization-measures`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// V2 API Response wrapper type
type ApiResponse<T> = {
  status: 'success' | 'fail'
  code: number
  message: string
  data?: T
  error?: string
}

// Types matching our backend API
export type Measure = {
  id: number
  measureName: string
  status: number
  color?: string
  score?: number
  tier?: string
  definition?: string
  individualBehavior?: string
  teamImpact?: string
  organizationalUse?: string
  personalDevelopment?: string
}

export type JournalPrompt = {
  id: number
  measureId?: number
  promptText: string
  category?: string
  difficulty: string
  timeEstimate: number
  isActive: boolean
  measure?: Measure
}

// Grouped prompt types for weekly journal prompts
export type PromptItem = {
  id: number
  measureId: number
  promptText: string
  category: string
  difficulty: string
}

export type PromptGroup = {
  groupId: number
  weekNumber: number
  mainPrompt: PromptItem
  subPrompts: PromptItem[]
}

export type GroupedPromptsResponse = {
  prompts: PromptGroup | PromptGroup[] | null
  totalWeeks: number
  currentWeek?: number
}

export type JournalEntry = {
  id: number
  userId: number
  promptId: number
  response: string
  createdOn: string
  timeSpent?: number
  mood?: string
  tags?: string[]
}

export type Scenario = {
  id: number
  measureId: number
  scenario: string  // The scenario text/story
  scenarioText?: string  // Legacy alias for scenario
  choiceA: string
  choiceB: string
  choiceC: string
  choiceD: string
  correctChoice: 'A' | 'B' | 'C' | 'D'
  explanation?: string
  feedbackA?: string
  feedbackB?: string
  feedbackC?: string
  feedbackD?: string
  measure?: Measure
}

export type ScenarioCompletion = {
  id: number
  userId: number
  scenarioId: number
  selectedChoice: 'A' | 'B' | 'C' | 'D'
  isCorrect: boolean
  completedAt: string
  timeSpent?: number
}

export type MicrolearningStats = {
  totalJournalEntries: number
  totalScenariosCompleted: number
  currentStreak: number
  averageScore: number
  completionRate: number
  lastActivityDate?: string
}

// Dashboard-friendly measure type with UI-specific fields
export type DashboardMeasure = {
  id: number
  name: string
  code: string
  color: string
  description: string
  category: string | null
  isActive: boolean
  // These are user-specific and populated from user scores
  score?: number
  tier?: 'strongest' | 'strong' | 'developing' | 'weakest'
}

// User measure score from API
export type UserMeasureScore = {
  id: number
  measureId: number
  measureName: string
  measureCode: string | null
  description: string | null
  category: string | null
  score: number
  tier: 'strongest' | 'strong' | 'developing' | 'weakest'
  isSuperPower: boolean
  isMicroLearning: boolean
}

// API response type for measures list
type MeasuresApiResponse = {
  status: 'success' | 'error'
  code: number
  message: string
  data?: Array<{
    id: number
    measureName: string
    measureCode: string
    description: string | null
    category: string | null
    isActive: boolean
    createdOn: string
  }>
}

// Organization measure from API
type OrganizationMeasure = {
  id: number
  organizationId: number
  measureId: number
  isActive: boolean
  customValue?: number | null
  customTarget?: number | null
  createdOn: string | null
  measure: {
    id: number
    measureName: string
    measureCode?: string | null
    description?: string | null
  }
}

// API response type for organization measures
type OrgMeasuresApiResponse = {
  status: 'success' | 'error'
  code: number
  message: string
  data?: OrganizationMeasure[]
}

// Predefined colors for measures (consistent with existing UI)
const MEASURE_COLORS: Record<number, string> = {
  1: "#FF6B6B",  // Advanced Strategy
  2: "#4ECDC4",  // Breadth of Approach
  3: "#45B7D1",  // Collaboration
  4: "#96CEB4",  // Communication
  5: "#FFEAA7",  // Conflict Approach
  6: "#DDA0DD",  // Conflict Response
  7: "#F8C471",  // Conflict Resolution
  8: "#85C1E9",  // Creative Decisiveness
  9: "#F7DC6F",  // Creative Flexibility
  10: "#BB8FCE", // Crisis Preparedness
  11: "#F1948A", // Crisis Response
  12: "#82E0AA", // Crisis Recovery
  13: "#AED6F1", // Dependability
  14: "#F8D7DA", // Developmental Orientation
  15: "#D5DBDB", // Emotional Resilience
  16: "#FADBD8", // Empathetic Communication
  17: "#D1F2EB", // Goal Management
  18: "#FCF3CF", // Goal Orientation
  19: "#EBDEF0", // Information Management
  20: "#D6EAF8", // Initiative
  21: "#E8F8F5", // Innovation
  22: "#FEF9E7", // People-Centrism
  23: "#FDEDEC", // Proactivity
  24: "#EBF5FB", // Productivity
  25: "#E8F6F3", // Resource Management
  26: "#FEF5E7", // Strategic Approach
  27: "#F4ECF7", // Strategic Quantitative Reasoning
  28: "#E3F2FD", // Team Orientation
  29: "#E8F5E8", // Task Management
  30: "#FFF3E0", // Workplace Engagement
}

// Get a color for a measure (fallback to generated color)
const getMeasureColor = (measureId: number): string => {
  if (MEASURE_COLORS[measureId]) {
    return MEASURE_COLORS[measureId]
  }
  // Generate a consistent color from ID
  const hue = (measureId * 137) % 360
  return `hsl(${hue}, 70%, 80%)`
}

// API Service Class
export class UnifiedLearningApiService {

  /**
   * Get all measures from the API
   * GET /saas/measures
   */
  async getMeasures(includeInactive: boolean = false): Promise<DashboardMeasure[]> {
    const params = includeInactive ? '?includeInactive=true' : ''
    const response = await measuresApi.get<MeasuresApiResponse>(`/${params}`)

    if (!response.data.data) {
      return []
    }

    return response.data.data.map(measure => ({
      id: measure.id,
      name: measure.measureName,
      code: measure.measureCode,
      color: getMeasureColor(measure.id),
      description: measure.description || '',
      category: measure.category,
      isActive: measure.isActive,
    }))
  }

  /**
   * Get current user's measure scores
   * GET /saas/measures/user-scores
   */
  async getUserMeasureScores(): Promise<UserMeasureScore[]> {
    const response = await measuresApi.get<ApiResponse<UserMeasureScore[]>>('/user-scores')

    if (!response.data.data) {
      return []
    }

    return response.data.data
  }

  /**
   * Get measures with user scores combined
   * Fetches both measures and user scores, merging them together
   */
  async getMeasuresWithScores(): Promise<DashboardMeasure[]> {
    // Fetch measures and user scores in parallel
    const [measures, userScores] = await Promise.all([
      this.getMeasures(),
      this.getUserMeasureScores()
    ])

    // Create a map of user scores by measureId
    const scoreMap = new Map(userScores.map(score => [score.measureId, score]))

    // Merge user scores into measures
    return measures.map(measure => {
      const userScore = scoreMap.get(measure.id)
      return {
        ...measure,
        score: userScore?.score,
        tier: userScore?.tier,
      }
    })
  }

  /**
   * Get measures for a specific organization with user scores
   * Only returns measures that the organization has unlocked (max 30)
   * @param organizationId The organization ID
   */
  async getMeasuresForOrganization(organizationId: number): Promise<DashboardMeasure[]> {
    // Fetch organization measures and user scores in parallel
    const [orgMeasuresResponse, userScores] = await Promise.all([
      orgMeasuresApi.get<OrgMeasuresApiResponse>(`/organization/${organizationId}`),
      this.getUserMeasureScores()
    ])

    const orgMeasures = orgMeasuresResponse.data.data || []

    // Filter to only active measures and limit to 30
    const activeMeasures = orgMeasures
      .filter(om => om.isActive)
      .slice(0, 30) // Enforce max 30 measures

    // Create a map of user scores by measureId
    const scoreMap = new Map(userScores.map(score => [score.measureId, score]))

    // Transform to DashboardMeasure format
    return activeMeasures.map(om => {
      const userScore = scoreMap.get(om.measureId)
      return {
        id: om.measureId,
        name: om.measure.measureName,
        code: om.measure.measureCode || '',
        color: getMeasureColor(om.measureId),
        description: om.measure.description || '',
        category: null,
        isActive: om.isActive,
        score: userScore?.score,
        tier: userScore?.tier,
      }
    })
  }

  /**
   * Get user's focus measure
   * POST /saas/microlearning/focus-measure/get
   * Returns the focus measure with createdOn date for time-based unlocking
   */
  async getUserFocusMeasure(userId: number): Promise<(Measure & { createdOn?: string }) | null> {
    const response = await api.post<ApiResponse<(Measure & { createdOn?: string }) | null>>('/focus-measure/get', { userId })
    return response.data.data || null
  }

  /**
   * Set user's focus measure
   * POST /saas/microlearning/focus-measure/set
   * Limited to 3 changes per year
   */
  async setUserFocusMeasure(userId: number, measureId: number): Promise<Measure & { remainingChangesThisYear?: number }> {
    try {
      const response = await api.post<ApiResponse<Measure & { remainingChangesThisYear?: number }>>('/focus-measure/set', { userId, measureId })
      if (!response.data.data) {
        throw new Error(response.data.message || 'Failed to set focus measure')
      }
      return response.data.data
    } catch (error) {
      // Re-throw with the error code if it's a measure change limit error
      const axiosError = error as { response?: { data?: { errorCode?: string; message?: string } } }
      if (axiosError.response?.data?.errorCode === 'MEASURE_CHANGE_LIMIT_REACHED') {
        const limitError = new Error(axiosError.response.data.message) as Error & { code?: string }
        limitError.code = 'MEASURE_CHANGE_LIMIT_REACHED'
        throw limitError
      }
      throw error
    }
  }

  /**
   * Get remaining focus measure changes for this year
   * GET /saas/microlearning/focus-measure/remaining-changes
   */
  async getRemainingMeasureChanges(userId: number): Promise<{
    changesThisYear: number
    remainingChanges: number
    maxChangesPerYear: number
  }> {
    const response = await api.get<ApiResponse<{
      changesThisYear: number
      remainingChanges: number
      maxChangesPerYear: number
    }>>(`/focus-measure/remaining-changes?userId=${userId}`)
    return response.data.data || { changesThisYear: 0, remainingChanges: 3, maxChangesPerYear: 3 }
  }

  /**
   * Get journal prompts for a measure
   * GET /saas/microlearning/journal/prompts?measureId=X
   */
  async getJournalPrompts(options: {
    measureId: number
    category?: string
    difficulty?: string
    limit?: number
  }): Promise<JournalPrompt[]> {
    const params = new URLSearchParams()
    params.append('measureId', options.measureId.toString())
    if (options.category) params.append('category', options.category)
    if (options.difficulty) params.append('difficulty', options.difficulty)
    if (options.limit) params.append('limit', options.limit.toString())

    const response = await api.get<ApiResponse<JournalPrompt[]>>(`/journal/prompts?${params}`)
    return response.data.data || []
  }

  /**
   * Get grouped journal prompts for a measure
   * Returns prompts organized into groups (main prompt + sub-prompts) for each week
   * GET /saas/microlearning/journal/prompts/grouped?measureId=X&weekNumber=Y
   */
  async getGroupedPrompts(options: {
    measureId: number
    weekNumber?: number
    groupSize?: number
  }): Promise<GroupedPromptsResponse> {
    const params = new URLSearchParams()
    params.append('measureId', options.measureId.toString())
    if (options.weekNumber) params.append('weekNumber', options.weekNumber.toString())
    if (options.groupSize) params.append('groupSize', options.groupSize.toString())

    const response = await api.get<ApiResponse<GroupedPromptsResponse>>(`/journal/prompts/grouped?${params}`)
    return response.data.data || { prompts: null, totalWeeks: 0 }
  }

  /**
   * Get user's journal entries
   * POST /saas/microlearning/journal/entries
   */
  async getJournalEntries(options: {
    userId: number
    measureId?: number
    limit?: number
    offset?: number
  }): Promise<JournalEntry[]> {
    const response = await api.post<ApiResponse<JournalEntry[]>>('/journal/entries', options)
    return response.data.data || []
  }

  /**
   * Create a new journal entry
   * POST /saas/microlearning/journal/create
   */
  async createJournalEntry(data: {
    userId: number
    promptId: number
    measureId: number
    response: string
    timeSpent?: number
    mood?: string
    tags?: string[]
  }): Promise<JournalEntry> {
    // Map frontend 'response' to backend 'entryText'
    const payload = {
      userId: data.userId,
      promptId: data.promptId,
      measureId: data.measureId,
      entryText: data.response,
      timeSpent: data.timeSpent,
      mood: data.mood,
      tags: data.tags
    }
    const response = await api.post<ApiResponse<JournalEntry>>('/journal/create', payload)
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to create journal entry')
    }
    return response.data.data
  }

  /**
   * Update a journal entry
   * POST /saas/microlearning/journal/update
   */
  async updateJournalEntry(options: {
    entryId: number
    userId: number
    response?: string
    mood?: string
    tags?: string[]
  }): Promise<JournalEntry> {
    const response = await api.post<ApiResponse<JournalEntry>>('/journal/update', options)
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to update journal entry')
    }
    return response.data.data
  }

  /**
   * Get scenario templates
   * GET /saas/microlearning/scenarios/templates?measureId=X
   */
  async getScenarioTemplates(options: {
    measureId: number
    limit?: number
  }): Promise<Scenario[]> {
    const params = new URLSearchParams()
    params.append('measureId', options.measureId.toString())
    if (options.limit) params.append('limit', options.limit.toString())

    const response = await api.get<ApiResponse<Scenario[]>>(`/scenarios/templates?${params}`)
    return response.data.data || []
  }

  /**
   * Get assigned scenarios for a user
   * POST /saas/microlearning/scenarios/assigned
   */
  async getAssignedScenarios(options: {
    userId: number
    measureId?: number
    includeCompleted?: boolean
  }): Promise<Scenario[]> {
    const response = await api.post<ApiResponse<Scenario[]>>('/scenarios/assigned', options)
    return response.data.data || []
  }

  /**
   * Assign a scenario to a user
   * POST /saas/microlearning/scenarios/assign
   */
  async assignScenario(data: {
    userId: number
    scenarioTemplateId: number
    scheduledFor?: string
  }): Promise<{ scenarioId: number }> {
    const response = await api.post<ApiResponse<{ scenarioId: number }>>('/scenarios/assign', data)
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to assign scenario')
    }
    return response.data.data
  }

  /**
   * Complete a scenario
   * POST /saas/microlearning/scenarios/complete
   */
  async completeScenario(data: {
    userId: number
    scenarioId: number
    selectedChoice: 'A' | 'B' | 'C' | 'D'
    timeSpent?: number
  }): Promise<{
    completion: ScenarioCompletion
    isCorrect: boolean
    explanation: string
  }> {
    const response = await api.post<ApiResponse<{
      completion: ScenarioCompletion
      isCorrect: boolean
      explanation: string
    }>>('/scenarios/complete', data)
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to complete scenario')
    }
    return response.data.data
  }

  /**
   * Get scenario completion history
   * POST /saas/microlearning/scenarios/history
   */
  async getScenarioHistory(options: {
    userId: number
    measureId?: number
    limit?: number
    offset?: number
  }): Promise<ScenarioCompletion[]> {
    const response = await api.post<ApiResponse<ScenarioCompletion[]>>('/scenarios/history', options)
    return response.data.data || []
  }

  /**
   * Get scenario statistics
   * POST /saas/microlearning/scenarios/stats
   */
  async getScenarioStats(userId: number, measureId?: number): Promise<{
    totalCompleted: number
    totalCorrect: number
    averageScore: number
    byMeasure: { measureId: number; measureName: string; completed: number; correct: number }[]
  }> {
    const response = await api.post<ApiResponse<{
      totalCompleted: number
      totalCorrect: number
      averageScore: number
      byMeasure: { measureId: number; measureName: string; completed: number; correct: number }[]
    }>>('/scenarios/stats', { userId, measureId })
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to get scenario stats')
    }
    return response.data.data
  }

  /**
   * Get microlearning statistics
   * POST /saas/microlearning/stats
   */
  async getMicrolearningStats(userId: number, measureId?: number): Promise<MicrolearningStats> {
    const response = await api.post<ApiResponse<MicrolearningStats>>('/stats', { userId, measureId })
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to get stats')
    }
    return response.data.data
  }

  /**
   * Get delivery preferences
   * POST /saas/microlearning/delivery/preferences/get
   */
  async getDeliveryPreferences(userId: number): Promise<{
    deliveryMethod: 'email' | 'sms' | 'both'
    frequency: 'daily' | 'weekly' | 'biweekly'
    preferredTime?: string
  }> {
    const response = await api.post<ApiResponse<{
      deliveryMethod: 'email' | 'sms' | 'both'
      frequency: 'daily' | 'weekly' | 'biweekly'
      preferredTime?: string
    }>>('/delivery/preferences/get', { userId })
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to get delivery preferences')
    }
    return response.data.data
  }

  /**
   * Update delivery preferences
   * POST /saas/microlearning/delivery/preferences/update
   */
  async updateDeliveryPreferences(data: {
    userId: number
    deliveryMethod?: 'email' | 'sms' | 'both'
    frequency?: 'daily' | 'weekly' | 'biweekly'
    preferredTime?: string
    timezone?: string
    phoneNumber?: string
    email?: string
  }): Promise<void> {
    await api.post('/delivery/preferences/update', data)
  }

  /**
   * Get user's phone number
   * POST /saas/microlearning/phone/get
   */
  async getUserPhoneNumber(userId: number): Promise<{ phoneNumber: string | null }> {
    const response = await api.post<ApiResponse<{ phoneNumber: string | null }>>('/phone/get', { userId })
    if (!response.data.data) {
      throw new Error(response.data.message || 'Failed to get phone number')
    }
    return response.data.data
  }

  /**
   * Get notification logs
   * POST /saas/microlearning/notifications/logs
   */
  async getNotificationLogs(options: {
    userId: number
    limit?: number
    offset?: number
  }): Promise<{
    id: number
    userId: number
    type: 'email' | 'sms'
    status: 'sent' | 'failed' | 'pending'
    sentAt: string
    content?: string
  }[]> {
    const response = await api.post<ApiResponse<{
      id: number
      userId: number
      type: 'email' | 'sms'
      status: 'sent' | 'failed' | 'pending'
      sentAt: string
      content?: string
    }[]>>('/notifications/logs', options)
    return response.data.data || []
  }

  // Helper methods for backward compatibility with existing dashboard integration

  /**
   * Get a random journal prompt (using first available prompt)
   */
  async getRandomJournalPrompt(measureId?: number): Promise<JournalPrompt | null> {
    if (!measureId) {
      throw new Error('measureId is required for V2 API')
    }
    const prompts = await this.getJournalPrompts({ measureId, limit: 1 })
    return prompts.length > 0 ? prompts[0] : null
  }

  /**
   * Get scenarios (alias for getScenarioTemplates)
   */
  async getScenarios(measureId: number): Promise<Scenario[]> {
    return this.getScenarioTemplates({ measureId })
  }

  /**
   * Get a random scenario (using first available template)
   */
  async getRandomScenario(measureId: number): Promise<Scenario | null> {
    const scenarios = await this.getScenarioTemplates({ measureId, limit: 1 })
    return scenarios.length > 0 ? scenarios[0] : null
  }

  /**
   * Submit scenario response (alias for completeScenario)
   */
  async submitScenarioResponse(data: {
    userId: number
    scenarioId: number
    selectedChoice: 'A' | 'B' | 'C' | 'D'
    timeSpent?: number
  }): Promise<{ isCorrect: boolean; score: number; explanation: string }> {
    const result = await this.completeScenario(data)
    return {
      isCorrect: result.isCorrect,
      score: result.isCorrect ? 1 : 0,
      explanation: result.explanation
    }
  }

  /**
   * Format journal prompts for dashboard (backward compatibility)
   */
  async getFormattedJournalPrompts(measureId: number): Promise<Array<{
    id: number
    measureId?: number
    measureName?: string
    prompt: string
    category: string
  }>> {
    const prompts = await this.getJournalPrompts({ measureId })

    return prompts.map(prompt => ({
      id: prompt.id,
      measureId: prompt.measureId,
      measureName: prompt.measure?.measureName || 'General',
      prompt: prompt.promptText,
      category: prompt.category || 'reflection'
    }))
  }

  /**
   * Format scenarios for dashboard (backward compatibility)
   */
  async getFormattedScenarios(measureId: number): Promise<Array<{
    id: number
    measureId: number
    measureName: string
    title: string
    story: string
    choices: Array<{
      id: string
      text: string
      feedback: string
      isOptimal: boolean
    }>
  }>> {
    const scenarios = await this.getScenarioTemplates({ measureId })

    return scenarios.map(scenario => ({
      id: scenario.id,
      measureId: scenario.measureId,
      measureName: scenario.measure?.measureName || 'Unknown',
      title: `${scenario.measure?.measureName || 'Competency'} Challenge`,
      story: scenario.scenario || scenario.scenarioText || '',
      choices: [
        {
          id: 'a',
          text: scenario.choiceA,
          feedback: scenario.feedbackA || scenario.explanation || 'This option could be improved.',
          isOptimal: scenario.correctChoice === 'A'
        },
        {
          id: 'b',
          text: scenario.choiceB,
          feedback: scenario.feedbackB || scenario.explanation || 'This option could be improved.',
          isOptimal: scenario.correctChoice === 'B'
        },
        {
          id: 'c',
          text: scenario.choiceC,
          feedback: scenario.feedbackC || scenario.explanation || 'This option could be improved.',
          isOptimal: scenario.correctChoice === 'C'
        },
        {
          id: 'd',
          text: scenario.choiceD,
          feedback: scenario.feedbackD || scenario.explanation || 'This option could be improved.',
          isOptimal: scenario.correctChoice === 'D'
        }
      ]
    }))
  }
}

// Export singleton instance
export const unifiedLearningApi = new UnifiedLearningApiService()
export default unifiedLearningApi
